// CRM photo uploader — uses the server-side /api/cloudinary/upload proxy
// (no client-side signature). Includes the Cloudinary library picker.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Library, Search, X, Check, RefreshCw } from "lucide-react";
import { api } from "../../lib/api";

export default function PhotoUploader({ photos, onChange, max = 30, hint = "first photo is the avatar" }) {
  const [uploading, setUploading] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const next = [...photos];
    setUploading(files.length);
    let done = 0;
    for (const f of files) {
      if (next.length >= 30) { toast.error("Max 30 photos"); break; }
      if (f.size > 50 * 1024 * 1024) { toast.error(`${f.name} > 50MB — skipped`); done++; setUploading(files.length - done); continue; }
      try {
        const form = new FormData();
        form.append("file", f);
        form.append("folder", "crm/photos");
        form.append("resource_type", "auto");
        const r = await api.post("/api/cloudinary/upload", form);
        next.push(r.data.secure_url);
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || String(err);
        toast.error(`${f.name}: ${msg}`);
      }
      done++;
      setUploading(files.length - done);
    }
    onChange(next);
    setUploading(0);
    e.target.value = "";
  };

  const removeAt = (i) => onChange(photos.filter((_, j) => j !== i));
  const setPrimary = (i) => {
    const next = [...photos]; const [pri] = next.splice(i, 1); onChange([pri, ...next]);
  };

  return (
    <div className="mt-4">
      <div className="text-[12px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--gio-muted)" }}>
        Photos · {photos.length}/30 {uploading > 0 && <span className="ml-2 text-[color:var(--gio-primary)]">uploading {uploading}…</span>}
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 mb-2">
        {photos.map((src, i) => (
          <div key={i} className="relative group rounded overflow-hidden border" style={{ borderColor: i === 0 ? "var(--gio-primary)" : "var(--gio-border)" }} data-testid={`crm-photo-${i}`}>
            <img src={src} alt="" className="w-full h-24 object-cover" loading="lazy" />
            {i === 0 && (
              <span className="absolute top-1 left-1 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded" style={{ background: "var(--gio-primary)", color: "#fff" }}>PRIMARY</span>
            )}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-1" style={{ background: "rgba(0,0,0,0.4)" }}>
              {i !== 0 && <button onClick={(e) => { e.stopPropagation(); setPrimary(i); }} className="text-[10px] bg-white px-2 py-1 rounded font-bold">Primary</button>}
              <button onClick={(e) => { e.stopPropagation(); removeAt(i); }} className="text-white" data-testid={`crm-photo-remove-${i}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {photos.length < 30 && (
          <>
            <label className="flex flex-col items-center justify-center h-24 rounded border-2 border-dashed cursor-pointer hover:bg-[var(--gio-primary-soft)]" style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted-2)" }} data-testid="crm-photo-upload">
              <Plus size={20} />
              <span className="text-[11px] font-bold uppercase mt-1">Upload</span>
              <input type="file" accept="image/*,.heic,.heif" multiple onChange={onFile} className="hidden" />
            </label>
            <button type="button" onClick={() => setPickerOpen(true)} data-testid="crm-photo-pick-cloudinary"
              className="flex flex-col items-center justify-center h-24 rounded border-2 border-dashed cursor-pointer transition hover:bg-[var(--gio-primary-soft)] hover:border-[color:var(--gio-secondary)]"
              style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted-2)" }}>
              <Library size={20} />
              <span className="text-[11px] font-bold uppercase mt-1">Library</span>
            </button>
          </>
        )}
      </div>
      <div className="text-[11px]" style={{ color: "var(--gio-muted-3)" }}>Hosted on Cloudinary · JPEG/PNG up to 10MB · {hint}</div>
      {pickerOpen && (
        <CloudinaryPicker
          onClose={() => setPickerOpen(false)}
          onPick={(urls) => {
            const next = [...photos];
            for (const u of urls) {
              if (next.length >= 8) break;
              if (!next.includes(u)) next.push(u);
            }
            onChange(next);
            setPickerOpen(false);
            toast.success(`Added ${urls.length} photo${urls.length !== 1 ? "s" : ""}`);
          }}
          maxSelection={8 - photos.length}
        />
      )}
    </div>
  );
}

// Cloudinary library picker — lists assets, supports folder filter + multi-select.
function CloudinaryPicker({ onClose, onPick, maxSelection = 8 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [folder, setFolder] = useState("crm/photos");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(async (cursor = null, append = false) => {
    if (cursor) setLoadingMore(true); else setLoading(true);
    try {
      const r = await api.get("/api/cloudinary/list", {
        params: { folder, max_results: 60, next_cursor: cursor || "" },
      });
      const newItems = r.data.items || [];
      if (append) setItems(prev => [...prev, ...newItems]);
      else setItems(newItems);
      setNextCursor(r.data.next_cursor || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Cloudinary library");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folder]);

  useEffect(() => { load(null, false); /* eslint-disable-next-line */ }, [folder]);

  // ESC to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const toggleSelect = (url) => {
    setSelected((prev) => {
      if (prev.includes(url)) return prev.filter(u => u !== url);
      if (prev.length >= maxSelection) {
        toast.error(`Max ${maxSelection} photos`);
        return prev;
      }
      return [...prev, url];
    });
  };

  const filtered = search
    ? items.filter(it => (it.public_id || "").toLowerCase().includes(search.toLowerCase()))
    : items;

  const FOLDER_PRESETS = [
    { v: "crm/photos", label: "CRM photos" },
    { v: "uploads", label: "Uploads" },
    { v: "submissions", label: "Submissions" },
    { v: "videos", label: "Videos" },
    { v: "", label: "All folders" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="cloudinary-picker">
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(10px)" }} />
      <div onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-5xl rounded-xl overflow-hidden flex flex-col max-h-[92vh]"
        style={{ background: "var(--gio-surface)", border: "1px solid var(--gio-border)" }}>
        {/* Header */}
        <div className="px-5 py-3 border-b flex items-center justify-between gap-3 flex-wrap" style={{ borderColor: "var(--gio-border)" }}>
          <div className="overline inline-flex items-center gap-2" style={{ color: "var(--gio-secondary)" }}>
            <Library size={12} /> CLOUDINARY LIBRARY · {items.length} loaded
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="cloudinary-picker-close"><X size={18} /></button>
        </div>
        {/* Filters */}
        <div className="px-5 py-3 border-b flex items-center gap-2 flex-wrap" style={{ borderColor: "var(--gio-border)" }}>
          <div className="flex flex-wrap gap-1.5">
            {FOLDER_PRESETS.map(f => (
              <button key={f.v} onClick={() => { setFolder(f.v); setSelected([]); }} type="button"
                data-testid={`cloudinary-folder-${f.v || "all"}`}
                className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded border transition"
                style={{
                  borderColor: folder === f.v ? "var(--gio-primary)" : "var(--gio-border)",
                  color: folder === f.v ? "var(--gio-primary)" : "var(--gio-muted)",
                  background: folder === f.v ? "var(--gio-primary-soft)" : "transparent",
                }}>
                {f.label}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[200px]">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: "var(--gio-muted-3)" }} />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by public_id…"
              data-testid="cloudinary-search"
              className="bg-black border pl-7 pr-3 py-1.5 text-[12px] rounded outline-none w-full font-mono-ui"
              style={{ borderColor: "var(--gio-border)", color: "var(--gio-text)" }} />
          </div>
        </div>
        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-12 text-[13px]" style={{ color: "var(--gio-muted-2)" }}>Loading library…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-[13px]" style={{ color: "var(--gio-muted-3)" }}>
              No images found in <span className="font-mono-ui">{folder || "all folders"}</span>.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2" data-testid="cloudinary-grid">
                {filtered.map(it => {
                  const isSelected = selected.includes(it.secure_url);
                  return (
                    <button key={it.public_id} onClick={() => toggleSelect(it.secure_url)} type="button"
                      data-testid={`cloudinary-asset-${it.public_id.replace(/[^a-z0-9]/gi, "_")}`}
                      className="relative aspect-square rounded overflow-hidden border-2 transition hover:-translate-y-0.5"
                      style={{
                        borderColor: isSelected ? "var(--gio-primary)" : "var(--gio-border)",
                        boxShadow: isSelected ? "0 0 0 2px var(--gio-primary)" : undefined,
                      }}>
                      <img src={it.secure_url.replace("/upload/", "/upload/c_fill,w_200,h_200,q_auto,f_auto/")}
                        alt="" className="w-full h-full object-cover" loading="lazy" />
                      {isSelected && (
                        <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(20,184,166,0.55)" }}>
                          <div className="w-7 h-7 rounded-full flex items-center justify-center bg-white">
                            <Check size={16} style={{ color: "var(--gio-primary)" }} />
                          </div>
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 px-1 py-0.5 text-[8px] font-mono-ui truncate"
                        style={{ background: "rgba(0,0,0,0.65)", color: "rgba(255,255,255,0.7)" }}>
                        {it.public_id.split("/").pop()}
                      </div>
                    </button>
                  );
                })}
              </div>
              {nextCursor && (
                <div className="text-center mt-4">
                  <button onClick={() => load(nextCursor, true)} disabled={loadingMore}
                    data-testid="cloudinary-load-more"
                    className="btn-ghost text-[12px] inline-flex items-center gap-1.5">
                    {loadingMore ? <RefreshCw size={11} className="animate-spin" /> : <Plus size={11} />}
                    {loadingMore ? "Loading…" : "Load more"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        {/* Footer */}
        <div className="px-5 py-3 border-t flex items-center justify-between gap-2" style={{ borderColor: "var(--gio-border)" }}>
          <div className="text-[12px]" style={{ color: "var(--gio-muted-2)" }}>
            <span className="font-bold" style={{ color: "var(--gio-text)" }}>{selected.length}</span> selected · max {maxSelection}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} type="button" data-testid="cloudinary-cancel" className="btn-ghost text-xs">Cancel</button>
            <button onClick={() => onPick(selected)} type="button" disabled={selected.length === 0}
              data-testid="cloudinary-confirm"
              className="btn-primary text-xs disabled:opacity-40">
              + Add {selected.length || ""}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
